import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { useAuth } from "../../auth/FirebaseAuth";
import { useAppContext } from "../../config/AppContext";
import { getOrRefreshFirebaseToken } from "../../config/getFirebaseToken";
import { getOrgDataById, getUserDataById } from "../../models/DataFunctions";
import SecondaryButton from "../../buttons/SecondaryButton";
import Column from "antd/es/table/Column";
import { EasySwitch } from "../../textfields/EasySwitch";
import TextField2 from "../../textfields/TextField2";
import WarningButton from "../../buttons/WarningButton";
import PrimaryButton from "../../buttons/PrimaryButton";
import Icon1 from "../../assets/icons/Icon";
import { useParams } from "react-router-dom";
import updateDocDataFieldApi from "../../api/database/updateDocDataFieldApi";
import { Divider } from "antd";

function User({ navigateWorkspace, handleLoginAs }) {
  const { id } = useParams(); // Access the `id` parameter from the URL
  const { currentUser, loading } = useAuth();

  const [userData, setUserData] = useState(null);
  const [orgData, setOrgData] = useState(null);
  const [actualUserData, setActualUserData] = useState(null);

  const { enqueueSnackbar } = useSnackbar();
  const handleClickVariant = (message, variant) => {
    enqueueSnackbar(message, { variant });
  };

  const { appState, setAppState, resetAppStateLogin } = useAppContext();
  const prefix = appState.dev ? appState.dev_url : appState.prod_url;
  const [activated_login_as, setActivatedLoginAs] = useState(false);
  const [confirm, setConfirm] = useState("");

  // Fetch user, org, clients, and article data
  const handleFetchData = async () => {
    if (currentUser) {
      try {
        // Fetch user data
        const user = await getUserDataById(currentUser.uid);
        if (user.org_id && user.org_id !== undefined) {
          // Fetch organization data using user's org reference
          const org = await getOrgDataById(user.org_id);

          const actual_user = await getUserDataById(id);
          // Update state with fetched data
          setUserData(user);
          setOrgData(org);
          setActualUserData(actual_user);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    }
  };

  useEffect(() => {
    if (currentUser) {
      handleFetchData(); // Fetch user data when currentUser is available
    }
  }, [currentUser]); // Dependency array ensures this runs when currentUser changes

  // Handle navigation to clients page
  const gotoUsers = () => {
    navigateWorkspace("/admin/users");
  };

  if (loading || !currentUser || !userData) {
    return <div></div>; // Show loading state while checking auth or if currentUser is null
  }

  return (
    <div className="flex flex-row w-full h-full overflow-auto ">
      {/* Left Column: Takes all available space */}
      <div className="flex flex-row   mt-4 gap-4 w-full">
        <div className="flex flex-col h-full w-full flex-grow pl-4">
          <div className="flex flex-row justify-between w-full">
            <div
              className="flex flex-row items-center gap-2"
              onClick={gotoUsers}
            >
              <div className="text-h2 font-semibold text-pr bg-transparent">
                Utilisateurs
              </div>
              <Icon1 icon_name="right" height="15px"></Icon1>
            </div>

            <div className="flex-grow flex-row-reverse flex w-full">
              <PrimaryButton
                className="w-fit"
                disabled={actualUserData?.email === "william.bresso@gmail.com"}
                onClick={() => {
                  handleLoginAs(id);
                }}
              >
                Login
              </PrimaryButton>
            </div>
          </div>
          <div className="text-hx font-semibold">
            {actualUserData?.display_name}
          </div>
          <div className="text-h3 font-light">
            {actualUserData?.tracking?.last_connection && (
              <>
                Dernière connexion le{" "}
                {new Date(
                  actualUserData?.tracking?.last_connection?.seconds * 1000
                ).toLocaleString()}
              </>
            )}
          </div>
          <div className="flex flex-col gap-2 mt-4">
            <WarningButton disabled={confirm !== "CONFIRMER"} className="w-fit">
              Delete User and Firebase Auth
            </WarningButton>
            <WarningButton disabled={confirm !== "CONFIRMER"} className="w-fit">
              Delete associated Org and All Users and All Firebase Auth and All
              Folders
            </WarningButton>
            <TextField2
              className="max-w-c200"
              label={"CONFIRMER"}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
            />
          </div>
          <div className="flex flex-row w-full items-center mt-4 gap-2">
            <EasySwitch
              checked={actualUserData?.maintenance_proof}
              onChange={async (checked) => {
                await updateDocDataFieldApi(
                  actualUserData.ref,
                  "maintenance_proof",
                  checked,
                  prefix
                );
                handleClickVariant("Maintenance proof updated", "success");
                handleFetchData();
              }}
            ></EasySwitch>
            <div className="text-h3 font-light">Maintenance Proof</div>
          </div>
          {/* Displaying members and affiliates */}
          {/* Client contact information */}
        </div>

        {/* Right Column: Fixed width */}
        <div className="flex flex-col h-full min-w-c400  pl-2">
          <div className="text-h1 font-semibold">Informations</div>
          <Divider orientation="vertical" />
          <div className="flex flex-col w-full pr-2">
            <InfoCell title="ID" data={actualUserData?.id} />
            <InfoCell title="OID" data={actualUserData?.org_id} />

            <InfoCell
              title="Connected"
              data={
                actualUserData?.tracking?.last_connection
                  ? new Date(
                      actualUserData?.tracking?.last_connection * 1000
                    ).toLocaleString()
                  : "Never"
              }
            />
            <InfoCell
              title="Display Name"
              data={actualUserData?.display_name}
            />
            <InfoCell title="Email" data={actualUserData?.email} />
            <InfoCell title="Phone" data={actualUserData?.phone} />
          </div>

          {/* Notes section */}
          <div className="text-h1 font-semibold">Notes</div>

          <div className="w-full flex flex-row-reverse px-3 py-1">
            <SecondaryButton>Enregistrer</SecondaryButton>
          </div>
        </div>
      </div>
    </div>
  );
}

export default User;

export function InfoCell({ title, data, editable = false }) {
  return (
    <div className="flex flex-row items-center gap-1 h-fit">
      {title && (
        <div className="text-h3 font-medium min-w-c100 w-c100 h-fit">
          {title}
        </div>
      )}
      <div
        className={`text-h3 rounded-md py-1 break-all h-fit min-h-7 w-c200 px-2 mr-2  flex-grow bg-shadow_4  font-light text-pr ${
          editable
            ? "cursor-pointer border-slate-200 hover:border-blue-400 border-2"
            : " "
        }`}
      >
        {data}
      </div>
    </div>
  );
}
